import { Link } from "react-router-dom";

export default function Terms() {
  const updated = "14 June 2026";
  return (
    <div className="min-h-screen bg-[#F3F0EA] text-[#2A332C]">
      <div className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <Link to="/" className="text-sm text-[#5C6B61] hover:text-[#3B5A3E]" data-testid="terms-back-link">
          ← Back
        </Link>
        <header className="mt-6 mb-10">
          <div className="ff-label">Legal</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-sm text-[#5C6B61] mt-3">Last updated: {updated}</p>
        </header>

        <section className="prose prose-sm max-w-none space-y-6 text-[15px] leading-relaxed" data-testid="terms-content">
          <p>
            These Terms govern your use of the Shop, Cal & Meal website (<strong>smartershop.xyz</strong>) and mobile app. By creating an account you agree to these Terms.
          </p>

          <Section title="1. The service">
            <p>
              Shop, Cal & Meal helps you discover recipes, scan food labels, plan meals, track calories and build shopping lists for major UK supermarkets. Some features (premium meal scoring, friend meal sharing, weekly recaps) are reserved for the Pro subscription.
            </p>
          </Section>

          <Section title="2. Eligibility">
            <p>You must be at least 13 to use Shop, Cal & Meal, and 18 (or have the consent of a parent / guardian) to buy a Pro subscription.</p>
          </Section>

          <Section title="3. Your account">
            <ul className="list-disc pl-5 space-y-2">
              <li>Give us accurate info on signup and keep your password safe.</li>
              <li>You&apos;re responsible for everything that happens under your account.</li>
              <li>You can delete your account at any time from <strong>Profile → Delete my account</strong>.</li>
            </ul>
          </Section>

          <Section title="4. Subscriptions (Pro)">
            <ul className="list-disc pl-5 space-y-2">
              <li>Pro is billed by <strong>Stripe</strong> in your local currency. Renewals are automatic until cancelled.</li>
              <li>You can cancel any time from <strong>Profile → Manage subscription</strong>. Cancellation stops future charges; you keep Pro access until the end of the paid period.</li>
              <li>Trial offers (e.g. 48-hour free trial) are one-per-user and may be withdrawn at our discretion.</li>
              <li>Statutory consumer rights are unaffected. UK / EU users have a 14-day cancellation right unless they expressly request immediate access (which most Shop, Cal & Meal users do by clicking &ldquo;Start now&rdquo;).</li>
            </ul>
          </Section>

          <Section title="5. Refunds">
            <p>
              We offer a no-questions-asked refund within 14 days of your first Pro payment if you haven&apos;t used Pro features extensively. Email <a href="mailto:hello@smartershop.xyz" className="text-[#3B5A3E] underline">hello@smartershop.xyz</a> to request one.
            </p>
          </Section>

          <Section title="6. AI features &amp; nutritional information">
            <p>
              Recipes, calorie estimates, basket health scores and substitute suggestions are produced with the help of AI (currently Anthropic Claude). They are best-effort estimates and <strong>not medical or dietary advice</strong>. Always check labels and consult a qualified professional for medical decisions.
            </p>
          </Section>

          <Section title="7. Shopping integrations">
            <p>
              Shop, Cal & Meal can pre-fill baskets at supermarket websites (Tesco, Sainsbury&apos;s, Asda, Morrisons, Waitrose, Aldi, Lidl, M&amp;S, Iceland). We don&apos;t complete the purchase for you &mdash; you review and check out on the retailer&apos;s site, subject to their terms.
            </p>
          </Section>

          <Section title="8. Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Reverse-engineer, scrape or hammer the API beyond fair use.</li>
              <li>Use the service to harass, defraud or breach others&apos; rights.</li>
              <li>Upload content you don&apos;t have the rights to.</li>
            </ul>
            <p>We may suspend or terminate accounts that breach these rules.</p>
          </Section>

          <Section title="9. Content &amp; intellectual property">
            <p>
              Shop, Cal & Meal, its name and design are our property. Recipes and meal data you save remain yours, but you grant us a worldwide, royalty-free licence to host and display them in the service. Public recipe URLs and store branding belong to their respective owners.
            </p>
          </Section>

          <Section title="10. Liability">
            <p>
              Shop, Cal & Meal is provided &ldquo;as is&rdquo;. We&apos;re not liable for indirect or consequential losses, missed deliveries by supermarkets, or any health decisions made on the basis of AI estimates. Nothing in these Terms limits liability that can&apos;t be limited by law (e.g. death or personal injury caused by negligence).
            </p>
          </Section>

          <Section title="11. Changes">
            <p>
              We&apos;ll post any material change here with a new &ldquo;Last updated&rdquo; date. Continued use after a change means you accept the new Terms.
            </p>
          </Section>

          <Section title="12. Governing law">
            <p>These Terms are governed by the laws of England &amp; Wales. Disputes go to the courts of England &amp; Wales unless local consumer law gives you a different right.</p>
          </Section>

          <Section title="13. Contact">
            <p>Questions? Email <a href="mailto:hello@smartershop.xyz" className="text-[#3B5A3E] underline">hello@smartershop.xyz</a>.</p>
          </Section>
        </section>

        <footer className="mt-12 pt-6 border-t border-[#E5E2DA] text-xs text-[#8B9690] flex items-center gap-3 flex-wrap">
          <span>© Shop, Cal & Meal</span>
          <span>·</span>
          <Link to="/privacy" className="hover:text-[#3B5A3E]" data-testid="terms-link-privacy">Privacy Policy</Link>
          <span>·</span>
          <a href="mailto:hello@smartershop.xyz" className="hover:text-[#3B5A3E]">hello@smartershop.xyz</a>
        </footer>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold text-[#2A332C]" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
      <div className="text-[#3C4842]">{children}</div>
    </section>
  );
}
